import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps {
  onSearchChange?: (query: string) => void;
  required?: boolean;
  'aria-describedby'?: string;
  options: SelectOption[] | SelectOptionGroup[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  /** Extra classes on the trigger button itself — `className` styles the wrapper. */
  triggerClassName?: string;
  id?: string;
  /** Accessible name for the trigger (its content is only the selected value). */
  'aria-label'?: string;
  /** @default "default" */
  size?: 'default' | 'sm';
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  renderOption?: (option: SelectOption, selected: boolean) => React.ReactNode;
  renderValue?: (option: SelectOption) => React.ReactNode;
  /**
   * Custom search predicate, called with the raw query. Defaults to a
   * case-insensitive substring match on `label` and `value`.
   */
  filterOption?: (option: SelectOption, query: string) => boolean;
  /** Extra classes applied to every dropdown option. */
  optionClassName?: string;
  /** Extra classes applied only to the selected dropdown option. */
  selectedOptionClassName?: string;
  /** Inline styles applied only to the selected dropdown option. */
  selectedOptionStyle?: React.CSSProperties;
  /** Render the dropdown inline instead of portaling to document.body. Use inside modal dialogs. */
  disablePortal?: boolean;
  /**
   * Extra classes on the dropdown panel. It is as wide as the trigger; a
   * `min-w-*` here lets a narrow trigger (a table cell) open a readable list.
   */
  dropdownClassName?: string;
  /**
   * Which trigger edge the dropdown lines up with. `end` makes a panel wider
   * than its trigger grow to the left, for triggers near the right edge.
   * @default "start"
   */
  align?: 'start' | 'end';
}

function isOptionGroup(
  option: SelectOption | SelectOptionGroup,
): option is SelectOptionGroup {
  return 'options' in option;
}

function defaultFilterOption(option: SelectOption, query: string): boolean {
  const q = query.toLowerCase();
  return (
    option.label.toLowerCase().includes(q) ||
    option.value.toLowerCase().includes(q)
  );
}

function flattenOptions(
  options: SelectOption[] | SelectOptionGroup[],
): SelectOption[] {
  if (options.length === 0) return [];
  if (isOptionGroup(options[0])) {
    return (options as SelectOptionGroup[]).flatMap((g) => g.options);
  }
  return options as SelectOption[];
}

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      onSearchChange,
      required,
      'aria-describedby': ariaDescribedBy,
      value: controlledValue,
      onChange,
      placeholder = 'Select...',
      disabled = false,
      error = false,
      className,
      triggerClassName,
      id,
      'aria-label': ariaLabel,
      size = 'default',
      searchable = false,
      searchPlaceholder = 'Search...',
      noResultsText = 'No results found.',
      renderOption,
      renderValue,
      filterOption = defaultFilterOption,
      optionClassName,
      selectedOptionClassName,
      selectedOptionStyle,
      disablePortal = false,
      dropdownClassName,
      align = 'start',
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState<
      string | undefined
    >();
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;

    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [highlightedIndex, setHighlightedIndex] = React.useState(0);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchRef = React.useRef<HTMLInputElement>(null);
    const listRef = React.useRef<HTMLUListElement>(null);
    const listboxId = React.useId();
    const [dropdownStyle, setDropdownStyle] =
      React.useState<React.CSSProperties>({
        position: 'fixed',
        top: 0,
        left: 0,
        visibility: 'hidden',
      });

    const isGrouped = options.length > 0 && isOptionGroup(options[0]);

    const allOptions = React.useMemo(() => flattenOptions(options), [options]);

    const filtered = React.useMemo(() => {
      if (!search) return allOptions;
      return allOptions.filter((o) => filterOption(o, search));
    }, [search, allOptions, filterOption]);

    const filteredGroups = React.useMemo(() => {
      if (!isGrouped) return null;
      const groups = options as SelectOptionGroup[];
      if (!search) return groups;
      return groups
        .map((g) => ({
          ...g,
          options: g.options.filter((o) => filterOption(o, search)),
        }))
        .filter((g) => g.options.length > 0);
    }, [isGrouped, options, search, filterOption]);

    const selectedOption = React.useMemo(
      () => allOptions.find((o) => o.value === value),
      [allOptions, value],
    );

    React.useEffect(() => {
      onSearchChange?.(search);
    }, [search, onSearchChange]);

    // Reset highlight when search changes
    React.useEffect(() => {
      setHighlightedIndex(0);
    }, [search]);

    // Close on outside click
    React.useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          containerRef.current &&
          !containerRef.current.contains(target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(target)
        ) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const updatePosition = React.useCallback(() => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const gap = 8;
      const margin = 8; // breathing room against the viewport edge
      const dropdownMaxHeight = 300; // max-h-60 = 15rem = 240px + search bar ~60px
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove =
        spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

      if (disablePortal) {
        setDropdownStyle({
          position: 'absolute',
          ...(placeAbove
            ? { bottom: '100%', marginBottom: gap }
            : { top: '100%', marginTop: gap }),
          ...(align === 'end' ? { right: 0 } : { left: 0 }),
          width: '100%',
          zIndex: 9999,
        });
      } else {
        // Shrink instead of spilling out of the viewport when neither side has
        // room for the full dropdown.
        const available = (placeAbove ? spaceAbove : spaceBelow) - gap - margin;
        setDropdownStyle({
          position: 'fixed',
          ...(placeAbove
            ? { bottom: window.innerHeight - rect.top + gap }
            : { top: rect.bottom + gap }),
          // `clientWidth`, not `innerWidth`: a fixed box's `right` is measured
          // from the viewport edge inside the vertical scrollbar.
          ...(align === 'end'
            ? { right: document.documentElement.clientWidth - rect.right }
            : { left: rect.left }),
          width: rect.width,
          maxHeight: Math.min(dropdownMaxHeight, Math.max(160, available)),
          zIndex: 9999,
          // Override the `pointer-events:none` a modal dialog sets on <body>
          // (Radix DismissableLayer) so the portaled dropdown stays clickable.
          pointerEvents: 'auto',
        });
      }
    }, [disablePortal, align]);

    // Position dropdown when opening (useLayoutEffect to avoid flash). A
    // portaled dropdown is viewport-anchored, so it has to be re-measured when
    // any ancestor scrolls (e.g. a scrollable dialog body) or the window resizes.
    React.useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
      if (disablePortal) return;
      const reflow = () => updatePosition();
      window.addEventListener('scroll', reflow, true); // capture: any ancestor
      window.addEventListener('resize', reflow);
      return () => {
        window.removeEventListener('scroll', reflow, true);
        window.removeEventListener('resize', reflow);
      };
    }, [open, disablePortal, updatePosition]);

    // Auto-focus search when opening
    React.useEffect(() => {
      if (open && searchable) {
        requestAnimationFrame(() =>
          searchRef.current?.focus({ preventScroll: true }),
        );
      }
      if (!open) {
        setSearch('');
      }
    }, [open, searchable]);

    // Scroll highlighted option into view
    React.useEffect(() => {
      if (!open || !listRef.current) return;
      const el = listRef.current.querySelector(
        `[data-option-index="${highlightedIndex}"]`,
      ) as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex, open]);

    // react-remove-scroll (used by Radix modal dialogs) cancels native wheel
    // events on body-portaled nodes, which would freeze this dropdown's scroll.
    // Scroll the list manually so it stays usable inside dialogs. A native
    // non-passive listener is required — React's onWheel is passive at the root,
    // so preventDefault there is ignored.
    React.useEffect(() => {
      const el = listRef.current;
      if (!open || !el) return;
      const onWheel = (e: WheelEvent) => {
        if (el.scrollHeight <= el.clientHeight) return; // nothing to scroll
        el.scrollTop += e.deltaY;
        e.preventDefault();
      };
      el.addEventListener('wheel', onWheel, { passive: false });
      return () => el.removeEventListener('wheel', onWheel);
    }, [open]);

    const selectValue = (optionValue: string) => {
      if (!isControlled) setInternalValue(optionValue);
      onChange?.(optionValue);
      setOpen(false);
      requestAnimationFrame(() => {
        const trigger = containerRef.current?.querySelector(
          'button[role="combobox"]',
        ) as HTMLButtonElement | null;
        trigger?.focus({ preventScroll: true });
      });
    };

    const navigateHighlight = (direction: 'up' | 'down') => {
      setHighlightedIndex((current) => {
        let next = current;
        const max = filtered.length - 1;
        do {
          next =
            direction === 'down'
              ? Math.min(next + 1, max)
              : Math.max(next - 1, 0);
        } while (
          filtered[next]?.disabled &&
          next !== current &&
          (direction === 'down' ? next < max : next > 0)
        );
        if (filtered[next]?.disabled) return current;
        return next;
      });
    };

    const handleSelect = () => {
      const option = filtered[highlightedIndex];
      if (option && !option.disabled) {
        selectValue(option.value);
      }
    };

    const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(true);
        }
        return;
      }

      if (!searchable) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            navigateHighlight('down');
            break;
          case 'ArrowUp':
            e.preventDefault();
            navigateHighlight('up');
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            handleSelect();
            break;
          case 'Escape':
            e.preventDefault();
            setOpen(false);
            break;
        }
      }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          navigateHighlight('down');
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateHighlight('up');
          break;
        case 'Enter':
          e.preventDefault();
          handleSelect();
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          requestAnimationFrame(() => {
            const trigger = containerRef.current?.querySelector(
              'button[role="combobox"]',
            ) as HTMLButtonElement | null;
            trigger?.focus({ preventScroll: true });
          });
          break;
      }
    };

    const renderOptionItem = (option: SelectOption, index: number) => {
      const isSelected = option.value === value;
      return (
        <li
          key={option.value}
          role="option"
          aria-selected={isSelected}
          aria-disabled={option.disabled}
          title={option.label}
          data-option-index={index}
          onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
          onClick={() => !option.disabled && selectValue(option.value)}
          style={isSelected ? selectedOptionStyle : undefined}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm',
            optionClassName,
            index === highlightedIndex && 'bg-muted',
            isSelected && selectedOptionClassName,
            option.disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {renderOption ? (
            renderOption(option, isSelected)
          ) : (
            <>
              <span className="flex-1 truncate">{option.label}</span>
              {isSelected && <Check className="size-4 text-riso-teal" />}
            </>
          )}
        </li>
      );
    };

    const renderOptions = () => {
      if (filtered.length === 0) {
        return (
          <li className="px-3 py-2 text-sm text-muted-foreground">
            {noResultsText}
          </li>
        );
      }

      if (isGrouped && filteredGroups) {
        let flatIndex = 0;
        return filteredGroups.map((group) => (
          <React.Fragment key={group.label}>
            <li
              role="presentation"
              className="px-3 py-1.5 text-xs font-semibold text-muted-foreground"
            >
              {group.label}
            </li>
            {group.options.map((option) => {
              const idx = flatIndex++;
              return renderOptionItem(option, idx);
            })}
          </React.Fragment>
        ));
      }

      return filtered.map((option, i) => renderOptionItem(option, i));
    };

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <button
          ref={ref}
          id={id}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-required={required}
          aria-describedby={ariaDescribedBy}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            'flex w-full items-center justify-between rounded-xl border-2 border-border bg-muted font-body transition-all duration-200 ease-spring enabled:hover:border-foreground/40 focus-visible:border-foreground focus-visible:shadow-hard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            size === 'default' && 'h-12 px-4 text-base',
            size === 'sm' && 'h-9 px-3 text-sm',
            error &&
              'border-destructive focus-visible:border-destructive focus-visible:shadow-[4px_4px_0_hsl(var(--destructive))]',
            !value && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate" title={selectedOption?.label}>
            {selectedOption
              ? renderValue
                ? renderValue(selectedOption)
                : selectedOption.label
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </button>

        {open &&
          (() => {
            const dropdown = (
              <div
                ref={dropdownRef}
                style={dropdownStyle}
                className={cn(
                  'flex animate-fade-in flex-col overflow-hidden rounded-xl border-2 border-border bg-card shadow-hard',
                  size === 'sm' && 'min-w-[10rem]',
                  dropdownClassName,
                )}
              >
                {searchable && (
                  <div className="shrink-0 border-b-2 border-border p-2">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        ref={searchRef}
                        type="text"
                        placeholder={searchPlaceholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        className="flex h-9 w-full rounded-lg bg-muted pl-8 pr-3 text-sm font-body placeholder:text-muted-foreground focus-visible:outline-none"
                      />
                    </div>
                  </div>
                )}
                <ul
                  ref={listRef}
                  id={listboxId}
                  role="listbox"
                  className="max-h-60 min-h-0 overflow-y-auto p-1"
                >
                  {renderOptions()}
                </ul>
              </div>
            );
            return disablePortal
              ? dropdown
              : createPortal(dropdown, document.body);
          })()}
      </div>
    );
  },
);
Select.displayName = 'Select';

export { Select };
